import React, { useState } from "react";
import { useEducation } from "../resume/useResumeData";
import DashboardLoader from "./DashboardLoader";
import Error from "../../ui/Error";
import EditSvg from "../../ui/EditSvg";
import DeleteSvg from "../../ui/DeleteSvg";
import EducationModal from "./EducationModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";



const EducationManager = () => {
    const { education, isLoading, deleteEducation, isDeleting } = useEducation();
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const handleDelete = (id) => {
        setDeleteId(id);
    };

    const handleConfirmDelete = () => {
        if (deleteId) {
            deleteEducation(deleteId, {
                onSuccess: () => setDeleteId(null)
            });
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingItem(null);
        setIsModalOpen(true);
    };

    if (isLoading) return <DashboardLoader />;
    if (!education) return <Error />;

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Education List</h1>
                <button
                    onClick={handleAddNew}
                    className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:translate-y-0 active:scale-95"
                >
                    <span className="relative z-10 flex items-center gap-2">
                        <svg className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Education
                    </span>
                    <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </button>
            </div>

            {education.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-16 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <p>No education entries yet — add your first one above.</p>
                </div>
            ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {education.map((item) => (
                    <div
                        key={item.id}
                        className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-[#1a1f2e] dark:text-white"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <h4 className="text-lg font-bold">{item.degree}</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {item.institution}
                                </p>
                                <div className="mt-1 flex items-center gap-2 text-xs">
                                    <span className="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-gray-800">
                                        {item.year_range}
                                    </span>
                                    {item.grade && (
                                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                            {item.grade}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleEdit(item)}
                                    className="transform rounded-full p-2 text-blue-500 transition-all duration-300 hover:bg-blue-50 hover:scale-110 hover:shadow-md dark:hover:bg-blue-900/20"
                                    title="Edit Education"
                                >
                                    <EditSvg className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => handleDelete(item.id)}
                                    disabled={isDeleting}
                                    className="transform rounded-full p-2 text-red-500 transition-all duration-300 hover:bg-red-50 hover:scale-110 hover:shadow-md dark:hover:bg-red-900/20"
                                    title="Delete Education"
                                >
                                    <DeleteSvg className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                        {item.description && (
                            <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
                                {item.description}
                            </p>
                        )}
                    </div>
                ))}
            </div>
            )}

            {isModalOpen && (
                <EducationModal
                    itemToEdit={editingItem}
                    onClose={() => setIsModalOpen(false)}
                />
            )}

            <DeleteConfirmationModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleConfirmDelete}
                title="this education entry"
                entityLabel="Education Entry"
            />
        </div>
    );
};

export default EducationManager;
